import { Controller } from "@hotwired/stimulus"

/**
 * Connect Wizard Controller
 * 
 * Multi-step wizard for creating action chains.
 * Steps:
 * 1. Select target action
 * 2. Configure chain (conditions, payload template)
 * 3. Confirm and create
 */
export default class extends Controller {
  static targets = [
    "step1", "step2", "step3",
    "step1Indicator", "step2Indicator", "step3Indicator",
    "backButton", "nextButton", "submitButton",
    "summarySource", "summaryTarget", "summaryTargetIcon", "summaryTargetType",
    "summaryStatus", "summaryConditions", "summaryPayload"
  ]
  
  static values = {
    actionId: Number,
    sourceType: String,
    sourceId: String
  }

  connect() {
    console.log('[ConnectWizard] Connected')
    this.currentStep = 1
    this.selectedTarget = null
    this.updateUI()
  }

  // Select a target action
  selectTarget(event) {
    const radio = event.target
    const label = radio.closest('label')
    const nameEl = label.querySelector('.text-white.font-medium')
    const typeEl = label.querySelector('.text-xs.text-gray-400')
    const iconContainer = label.querySelector('.w-10.h-10.rounded-lg')
    
    this.selectedTarget = {
      id: radio.value,
      name: nameEl?.textContent || 'Unknown',
      type: typeEl?.textContent || 'Action',
      iconBg: iconContainer?.className || ''
    }
    
    console.log('[ConnectWizard] Selected target:', this.selectedTarget)
    
    // Enable next button
    this.nextButtonTarget.disabled = false
    
    // Update summary
    this.updateSummary()
  }

  // Move to next step
  nextStep() {
    if (this.currentStep < 3) {
      this.currentStep++
      this.updateUI()
      this.updateSummary()
    }
  }

  // Move to previous step
  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--
      this.updateUI()
    }
  }

  // Update UI based on current step
  updateUI() {
    // Show/hide step content
    this.step1Target.classList.toggle('hidden', this.currentStep !== 1)
    this.step2Target.classList.toggle('hidden', this.currentStep !== 2)
    this.step3Target.classList.toggle('hidden', this.currentStep !== 3)
    
    // Update step indicators
    this.updateStepIndicator(this.step1IndicatorTarget, 1)
    this.updateStepIndicator(this.step2IndicatorTarget, 2)
    this.updateStepIndicator(this.step3IndicatorTarget, 3)
    
    // Show/hide navigation buttons
    this.backButtonTarget.classList.toggle('hidden', this.currentStep === 1)
    this.nextButtonTarget.classList.toggle('hidden', this.currentStep === 3)
    this.submitButtonTarget.classList.toggle('hidden', this.currentStep !== 3)
    
    // Disable next if no target selected on step 1
    if (this.currentStep === 1) {
      this.nextButtonTarget.disabled = !this.selectedTarget
    } else {
      this.nextButtonTarget.disabled = false
    }
  }

  updateStepIndicator(indicator, stepNum) {
    const circle = indicator.querySelector('div')
    const text = indicator.querySelector('span')
    
    if (stepNum < this.currentStep) {
      // Completed step
      circle.className = 'w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-medium flex items-center justify-center'
      circle.innerHTML = `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>`
      text.className = 'text-sm text-emerald-400 font-medium'
    } else if (stepNum === this.currentStep) {
      // Current step
      circle.className = 'w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-medium flex items-center justify-center'
      circle.textContent = stepNum
      text.className = 'text-sm text-white font-medium'
    } else {
      // Future step
      circle.className = 'w-6 h-6 rounded-full bg-gray-700 text-gray-400 text-xs font-medium flex items-center justify-center'
      circle.textContent = stepNum
      text.className = 'text-sm text-gray-400'
    }
  }

  // Update summary on step 3
  updateSummary() {
    if (!this.selectedTarget) return
    
    // Target name and type
    if (this.hasSummaryTargetTarget) {
      this.summaryTargetTarget.textContent = this.selectedTarget.name
    }
    if (this.hasSummaryTargetTypeTarget) {
      this.summaryTargetTypeTarget.textContent = this.selectedTarget.type
    }
    
    // Target icon (copy the background class)
    if (this.hasSummaryTargetIconTarget && this.selectedTarget.iconBg) {
      // Extract just the bg-* class
      const bgMatch = this.selectedTarget.iconBg.match(/bg-\w+-\d+/)
      if (bgMatch) {
        this.summaryTargetIconTarget.className = `w-10 h-10 rounded-lg ${bgMatch[0]} flex items-center justify-center`
      }
    }
    
    // Status
    if (this.hasSummaryStatusTarget) {
      const enabledCheckbox = this.element.querySelector('input[name="action_chain[enabled]"]')
      const isEnabled = enabledCheckbox?.checked !== false
      this.summaryStatusTarget.textContent = isEnabled ? 'Enabled' : 'Disabled'
      this.summaryStatusTarget.className = isEnabled ? 'text-emerald-400' : 'text-gray-400'
    }
    
    // Payload
    if (this.hasSummaryPayloadTarget) {
      const payloadTextarea = this.element.querySelector('textarea[name="action_chain[payload_template_json]"]')
      const hasPayload = payloadTextarea?.value?.trim().length > 0
      this.summaryPayloadTarget.textContent = hasPayload ? 'Custom template' : 'Pass-through'
    }
  }

  // Form submission
  submit(event) {
    // Allow normal form submission, but validate first
    if (!this.selectedTarget) {
      event.preventDefault()
      console.error('[ConnectWizard] No target selected')
      return
    }
    
    console.log('[ConnectWizard] Submitting chain creation')
  }
}
